import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Target, Users, Zap, Award } from 'lucide-react';
import { mockData } from '../mock/data';

const AboutPage = () => {
  const { team, stats } = mockData;

  const values = [
    {
      title: '30+ Years Experience',
      description: 'A founding team with proven expertise in scaling global digital campaigns.',
      icon: <Target className="w-8 h-8" />
    },
    {
      title: 'Certified Specialists',
      description: 'Google, Meta, Amazon, and DSP-certified professionals across platforms.',
      icon: <Users className="w-8 h-8" />
    },
    {
      title: 'AI-Powered Precision',
      description: 'Smarter targeting, budget optimization, and fraud detection at scale.',
      icon: <Zap className="w-8 h-8" />
    },
    {
      title: 'Expert Oversight',
      description: 'Dedicated Ops teams ensure AI-driven insights translate into real business outcomes.',
      icon: <Award className="w-8 h-8" />
    }
  ];

  const milestones = [
    {
      year: '2019',
      title: 'Company Founded',
      description: 'Started with a vision to revolutionize digital advertising operations for growing agencies.'
    },
    {
      year: '2020',
      title: 'First 100 Clients',
      description: 'Reached our first major milestone by helping 100+ agencies scale their operations efficiently.'
    },
    {
      year: '2022',
      title: 'AI Development Begins',
      description: 'Launched our AI research initiative to develop intelligent optimization and automation tools.'
    },
    {
      year: '2023',
      title: '$50M+ Ad Spend Managed',
      description: 'Crossed the milestone of managing over $50M in annual ad spend across our platform.'
    },
    {
      year: '2024',
      title: 'MarqiCast & MarqiRank Launch',
      description: 'Expanded our white-label suite with connected-TV advertising and AI search visibility for agencies.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>About Us - Marqi | AI-Powered Growth Agency</title>
        <meta name="description" content="Meet the AI-powered growth agency helping brands scale efficiently. 30+ years experience, certified specialists, and proven ROI optimization." />
        <meta name="keywords" content="about marqi, digital marketing agency, ai marketing experts, performance marketing specialists, advertising optimization" />
        <link rel="canonical" href="https://marqi.io/about" />
      </Helmet>
      
      <div className="min-h-screen py-20">
        <div className="container">
        {/* Hero Section Removed */}

        {/* Mission & Vision */}
        <section className="mb-24 bg-[var(--bg-secondary)] -mx-6 px-6 py-16 rounded-2xl">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="display-md text-[var(--text-primary)] mb-8" style={{ fontWeight: '400' }}>Our Mission</h2>
              <p className="body-lg mb-6">
                To redefine how brands grow by merging human intelligence with AI precision — delivering marketing efficiency, scalability, and performance once reserved for Fortune 500s to ambitious brands of all sizes.
              </p>
              <p className="body-md">
                We exist to make advanced advertising technology accessible, transparent, and results-driven — empowering businesses to scale profitably through smarter media operations and intelligent automation.
              </p>
            </div>
            
            <div>
              <h2 className="display-md text-[var(--text-primary)] mb-8" style={{ fontWeight: '400' }}>Our Vision</h2>
              <p className="body-lg mb-6">
                To become the world's most trusted white-label growth partner for agencies — where expert ops teams and AI-assisted execution work seamlessly together to eliminate inefficiency, unlock scale, and help agencies grow without adding headcount.
              </p>
              <p className="body-md">
                We envision a future where AI handles the complexity of advertising so humans can focus on creativity, strategy, and impact — not operations.
              </p>
            </div>
          </div>
        </section>

        {/* Company Stats Section Removed as Requested */}

        {/* Values */}
        <section className="mb-24">
          <div className="text-center mb-16">
            <h2 className="display-md text-[var(--text-primary)] mb-6" style={{ fontWeight: '400' }}>
              Why Choose Marqi
            </h2>
            <p className="body-lg max-w-3xl mx-auto">
              Our foundation of expertise, certification, and innovation ensures your advertising success.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div key={value.title} className={`text-center animate-fade-in`} style={{ animationDelay: `${index * 0.1}s` }}>
                <div className="w-16 h-16 bg-[var(--accent-bg)] rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <div className="text-[var(--accent-primary)]">{value.icon}</div>
                </div>
                <h3 className="h3 text-[var(--text-primary)] mb-4">{value.title}</h3>
                <p className="body-md">{value.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Founding Visionaries Section */}
        <section className="mb-24">
          <div className="text-center mb-16">
            <h2 className="display-md text-[var(--text-primary)] mb-6" style={{ fontWeight: '400' }}>
              Our Founding Visionaries
            </h2>
          </div>

          <div className="max-w-4xl mx-auto space-y-6">
            <p className="body-lg text-[var(--text-secondary)]">
              Marqi is built by a collective of senior professionals from global agencies, tech platforms, and AI research backgrounds — bringing together decades of experience in advertising, data science, and scalable operations.
            </p>
            
            <p className="body-lg text-[var(--text-secondary)]">
              While our core leadership remains in stealth mode during Marqi's early growth phase, our combined expertise from companies like Google, Meta, and top independent agencies drives every strategy and solution we deliver.
            </p>
            
            <p className="body-lg text-[var(--text-secondary)]">
              We're focused on building before we reveal — scaling a platform that speaks for itself through results, innovation, and impact.
            </p>
          </div>
        </section>

        {/* Company Timeline Section Removed as Requested */}

        {/* Awards & Recognition Section Removed as Requested */}

        {/* CTA Section */}
        <section className="text-center">
          <div className="feature-card">
            <h2 className="display-md text-[var(--text-primary)] mb-6" style={{ fontWeight: '400' }}>
              Ready to Transform Your Digital Advertising?
            </h2>
            <p className="body-lg mb-12 max-w-3xl mx-auto">
              Explore our live services — Marqi Ops, MarqiCast, and MarqiRank — delivering exceptional
              results through operational excellence, connected-TV reach, and AI search visibility.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Link to="/contact" className="btn-primary glow-effect">
                Get Started Today
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
              <Link to="/ops" className="btn-secondary">
                Explore Our Services
              </Link>
            </div>
          </div>
        </section>
      </div>
    </div>
    </>
  );
};

export default AboutPage;